import { useState } from 'react';
import { useEffect } from 'react';

import { Buffer } from 'buffer';
import cn from 'classnames';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import { useQueryParams } from 'hooks/query-params';
import ReactGA from 'react-ga4';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { SingleSignOnButton } from './components/SingleSignOnButton/SingleSignOnButton';
import { CodeInput } from 'components/CodeInput/CodeInput';
import { Button, FormMessage, Input, InputGroup } from 'components/FormComponents';
import { GoogleButton } from 'components/GoogleButton/GoogleButton';
import { Loading } from 'components/Loading/Loading';

import { login, loginWithGoogle, selectAuthentication, verify2fa } from 'api/authentication-slice';

import styles from './SignIn.module.scss';

const SignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { trackEvent } = useTracking();

  const authentication = useSelector(selectAuthentication);

  useDocumentTitle('Sign In');

  const navigate = useNavigate();

  const [redirect_uri, state] = useQueryParams(['redirect_uri', 'state']);

  const [loading, setLoading] = useState(false);
  const [messageText, setMessageText] = useState();

  const [userId, setUserId] = useState();
  const [mfa, setMfa] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'Sign In',
    });
  }, []);

  useEffect(() => {
    if (!authentication.checked) return;
    if (authentication.logged) redirect();
  }, [authentication]);

  const redirect = () => {
    if (redirect_uri) {
      const query_params = state ? Buffer.from(state, 'base64').toString() : '';
      window.location.replace(`${redirect_uri}${query_params}`);
    } else {
      navigate('/dashboard');
    }
  };

  const onGoogleSign = async ({ credential, clientId }) => {
    setLoading(true);
    const { payload: response } = await dispatch(
      loginWithGoogle({ credential, client_id: clientId }),
    );

    if (response.error) {
      setLoading(false);
      setMessageText(response.error.message);
    } else if (response.result.two_factor_auth_enabled) {
      setMfa(true);
      setUserId(response.result.user_id);
    } else {
      redirect();
    }
  };

  const onGoogleSignFailure = error => {
    console.error(error);
    setMessageText('Error getting google profile');
  };

  const onSubmit = async data => {
    setLoading(true);
    const { payload: response } = await dispatch(
      login({ email: data.email, password: data.password }),
    );
    setLoading(false);

    if (response.error) {
      setMessageText(response.error.message);
      return;
    } else if (response.result.two_factor_auth_enabled) {
      setMfa(true);
      setUserId(response.result.user_id);
    } else {
      redirect();
      trackEvent('User Signed In', {
        userId: response.result.user_id,
        userEmail: data.email,
        loginMethod: 'email',
      });
    }
  };

  const onMFASubmittion = async code => {
    setLoading(true);
    const result = await dispatch(verify2fa({ user_id: userId, code }));
    setLoading(false);

    if (result.verified) redirect();
    else setMessageText('Invalid code. Try again!');
  };

  return (
    <div className={styles.root}>
      <Loading active={loading} />
      <div className={cn('card u-margin-top', 'card-with-shadow', styles.card)}>
        <div className={cn('card_content', styles.content)}>
          <img
            onClick={() => window.location.replace('https://www.checkmate.tech')}
            className={cn(styles.logo, 'u-margin-bottom--large')}
            src="/assets/images/checkmate/icon-label.svg"
            alt="KnowMy Logo"
          />
          <h2 className="u-margin-bottom--large u-margin-top--0">
            <span className="display-2 u-font-weight-600 brand-gradient-text">
              {t('common.welcome')}&nbsp;
            </span>
            <span className="display-2 u-font-weight-400">{t('common.back')}</span>
          </h2>
          {mfa ? (
            <>
              <p>{t('signIn.twoFactorAuthentication.code')}</p>
              <CodeInput onComplete={onMFASubmittion} />
            </>
          ) : (
            <>
              <GoogleButton onLoginSuccess={onGoogleSign} onLoginFailure={onGoogleSignFailure} />
              <SingleSignOnButton className="u-margin-top" onClick={() => navigate('/sso')} />
              <div className={styles.divider}>
                <p>{t('common.or')}</p>
                <hr />
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="u-width-100 u-flex-box u-flex-align-center u-flex-direction-vertically">
                <FormMessage
                  message={messageText}
                  className="u-margin-top--0"
                  onClose={() => setMessageText(null)}
                />
                <InputGroup title="Email">
                  <Input
                    name="email"
                    icon="mail"
                    dataTestId="sign-in-email-input"
                    placeholder={t('signIn.emailPlaceholder')}
                    className="u-margin-bottom"
                    register={register}
                    validators={{ required: true }}
                    error={errors?.email?.type}
                    inputProps={{ tabIndex: 1 }}
                  />
                </InputGroup>
                <InputGroup title="Password">
                  <Link
                    className={cn('u-link', 'dark', 'u-margin-top--small', styles.forgotPassword)}
                    to="/forgot_password">
                    {t('signIn.forgotPassword')}
                  </Link>
                  <Input
                    name="password"
                    icon="lock"
                    type="password"
                    dataTestId="sign-in-password-input"
                    placeholder={t('signIn.enterPassword')}
                    register={register}
                    validators={{ required: true }}
                    error={errors?.password?.type}
                    inputProps={{ tabIndex: 2 }}
                  />
                </InputGroup>
                <Button
                  submit
                  dataTestId="sign-in-submit-button"
                  buttonProps={{ tabIndex: 3 }}
                  className="u-width-100 u-margin-top--large">
                  {t('signIn.login')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
